import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FaBars,
  FaCog,
  FaEnvelope,
  FaHome,
  FaMoon,
  FaSignInAlt,
  FaSun,
  FaTrophy,
  FaUserPlus,
  FaUsers,
} from "react-icons/fa";
import { MdOutlineMessage } from "react-icons/md";

import { useUser } from "../UserContext";
import { useMessage } from "../../../hooks/MessageContext";
import { useContactForm } from "../../../hooks/ContactFormContext";
import { useGroup } from "../../../hooks/GroupContext";
import { checkRoleAccess } from "../../../utils/roleHelper";

import { COACH_ROLES, NAVBAR4_CATEGORIES } from "./navbar4Config";

import Navbar4Brand from "./components/Navbar4Brand";
import Navbar4IconButton from "./components/Navbar4IconButton";
import Navbar4MegaMenu from "./components/Navbar4MegaMenu";
import Navbar4MobileMenu from "./components/Navbar4MobileMenu";
import Navbar4UserMenu from "./components/Navbar4UserMenu";

const getRoleId = (user) => {
  const value = user?.role_id ?? user?.roleId ?? user?.RoleId ?? user?.role?.id;
  const numeric = Number(value);

  return Number.isFinite(numeric) ? numeric : null;
};

const getRoleLabel = (user) => {
  const roleId = getRoleId(user);

  if (!user) return "visiteur";
  if (roleId === 1) return "administrateur";
  if (roleId === 3) return "Judoka(te)";
  if ([5, 6, 7].includes(roleId)) return "coach";
  return "judoka";
};

const Navbar4 = () => {
  const { user, updateUser } = useUser();

  const [menuOpen, setMenuOpen] = useState(false);
  const [theme, setTheme] = useState("light");

  const { unreadMessageCount } = useMessage();
  const { unreadCountContactForm } = useContactForm();
  const { pendingRequestsCount } = useGroup();

  const navigate = useNavigate();
  const location = useLocation();

  const roleId = getRoleId(user);
  const roleLabel = getRoleLabel(user);
  const isVisitor = !user;
  const isCoach = Boolean(user && roleId && checkRoleAccess(roleId, COACH_ROLES));
  const isAdmin = Number(roleId) === 1;

  const counts = useMemo(
    () => ({
      unreadMessageCount,
      unreadCountContactForm,
      pendingRequestsCount,
    }),
    [unreadMessageCount, unreadCountContactForm, pendingRequestsCount]
  );

  const hasAccess = useCallback(
    (roles = []) => {
      if (!roles || roles.length === 0) return true;
      if (!roleId) return false;
      return checkRoleAccess(roleId, roles);
    },
    [roleId]
  );

  const sections = useMemo(() => {
    return NAVBAR4_CATEGORIES.filter((section) => {
      if (isVisitor) return section.visitorOnly === true;
      if (section.visitorOnly) return false;
      if (section.adminOnly && !isAdmin) return false;
      if (section.coachOnly && !isCoach) return false;
      if (section.authOnly && !user) return false;
      return true;
    })
      .map((section) => ({
        ...section,
        items: section.items.filter((item) => hasAccess(item.roles)),
      }))
      .filter((section) => section.items.length > 0);
  }, [isVisitor, isAdmin, isCoach, user, hasAccess]);

  const quickLinks = useMemo(() => {
    if (!user) {
      return [
        { label: "Accueil", path: "/", icon: FaHome },
        { label: "Judo Expert", path: "/judo-expert", icon: FaTrophy },
        { label: "Coach Pro", path: "/espace-coach", icon: FaUsers },
        { label: "Tarifs", path: "/formules-tarifs", icon: FaTrophy },
      ];
    }

    if (isCoach) {
      return [
        { label: "Accueil", path: "/", icon: FaHome },
        { label: "Groupes", path: "/groups-coach", icon: FaUsers },
        { label: "Résultats", path: "/group-results", icon: FaTrophy },
      ];
    }

    return [
      { label: "Accueil", path: "/", icon: FaHome },
      { label: "Judo Expert", path: "/judo-expert", icon: FaTrophy },
      { label: "Compétitions", path: "/events-judoka", icon: FaTrophy },
      { label: "Résultats", path: "/resultats", icon: FaTrophy },
    ];
  }, [user, isCoach]);

  useEffect(() => {
    const storedUserInfo = localStorage.getItem("userInfo");

    if (storedUserInfo && !user) {
      try {
        const parsedUser = JSON.parse(storedUserInfo);
        if (parsedUser) updateUser(parsedUser);
      } catch (error) {
        console.error(
          "Erreur lors du parsing des informations de l'utilisateur",
          error
        );
      }
    }
  }, [user, updateUser]);

  useEffect(() => {
    const storedTheme =
      localStorage.getItem("theme") ||
      (document.documentElement.classList.contains("dark") ? "dark" : "light");

    setTheme(storedTheme);
    document.documentElement.classList.toggle("dark", storedTheme === "dark");
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    localStorage.setItem("theme", theme);
  }, [theme]);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";

    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const handleNavigate = (path, roles = []) => {
    if (roles?.length > 0 && !hasAccess(roles)) return;

    navigate(path);
    setMenuOpen(false);
  };

  const handleLogout = () => {
    localStorage.removeItem("userInfo");
    localStorage.removeItem("token");

    updateUser(null);
    setMenuOpen(false);
    navigate("/login");
  };

  const toggleTheme = () => {
    setTheme((prev) => (prev === "dark" ? "light" : "dark"));
  };

  return (
    <>
      <nav className="fixed inset-x-0 top-0 z-[70] h-[78px] border-b border-slate-200 bg-white text-slate-950 shadow-[0_8px_30px_rgba(15,23,42,0.08)] dark:border-white/10 dark:bg-[#070b14] dark:text-white">
        <div className="mx-auto flex h-full max-w-7xl items-center justify-between gap-4 px-4 sm:px-5 lg:px-6">
          <div className="flex min-w-0 items-center">
            <Navbar4Brand onClick={() => handleNavigate("/")} />
          </div>

          <div className="hidden items-center justify-center gap-1 md:flex">
            {quickLinks.map((link) => {
              const Icon = link.icon;
              const active = location.pathname === link.path;

              return (
                <button
                  key={link.path}
                  type="button"
                  onClick={() => handleNavigate(link.path)}
                  className={`inline-flex min-h-10 items-center gap-2 rounded-2xl px-4 py-2 text-sm font-bold transition-all ${
                    active
                      ? "bg-slate-950 text-white shadow-md dark:bg-white dark:text-slate-950"
                      : "text-slate-500 hover:bg-slate-100 hover:text-slate-950 dark:text-white/55 dark:hover:bg-white/[0.07] dark:hover:text-white"
                  }`}
                >
                  {Icon ? <Icon className="text-xs" /> : null}
                  {link.label}
                </button>
              );
            })}

            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              className="ml-2 inline-flex min-h-11 items-center gap-2 rounded-2xl bg-sky-600 px-5 py-2 text-sm font-bold text-white shadow-md shadow-sky-600/20 transition hover:-translate-y-0.5 hover:bg-sky-500"
            >
              <FaBars className="text-xs" />
              Menu
            </button>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            {user && (
              <Navbar4IconButton
                onClick={() => handleNavigate("/messagerie-judoka")}
                title="Mes messages"
                badge={unreadMessageCount}
                className="hidden xs:inline-flex"
              >
                <FaEnvelope />
              </Navbar4IconButton>
            )}

            {isAdmin && (
              <>
                <Navbar4IconButton
                  onClick={() => handleNavigate("/appli/dashboard")}
                  title="Messages de contact"
                  badge={unreadCountContactForm}
                  className="hidden sm:inline-flex"
                >
                  <MdOutlineMessage className="text-base" />
                </Navbar4IconButton>

                <Navbar4IconButton
                  onClick={() => handleNavigate("/appli/dashboard")}
                  title="Dashboard admin"
                  className="hidden sm:inline-flex"
                >
                  <FaCog />
                </Navbar4IconButton>
              </>
            )}

            {!user && (
              <>
                <button
                  type="button"
                  onClick={() => handleNavigate("/register")}
                  className="hidden min-h-10 items-center gap-2 rounded-2xl bg-gradient-to-r from-orange-500 to-yellow-400 px-4 py-2 text-sm font-bold text-slate-950 shadow-md shadow-orange-500/15 transition hover:-translate-y-0.5 sm:inline-flex"
                >
                  <FaUserPlus className="text-xs" />
                  Créer un compte
                </button>

                <Navbar4IconButton
                  onClick={() => handleNavigate("/login")}
                  title="Connexion"
                >
                  <FaSignInAlt />
                </Navbar4IconButton>
              </>
            )}

            <Navbar4IconButton
              onClick={toggleTheme}
              title={theme === "dark" ? "Mode clair" : "Mode sombre"}
            >
              {theme === "dark" ? <FaSun /> : <FaMoon />}
            </Navbar4IconButton>

            {user && (
              <div className="hidden sm:block">
                <Navbar4UserMenu
                  user={user}
                  onNavigate={handleNavigate}
                  onLogout={handleLogout}
                />
              </div>
            )}

            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              className="inline-flex h-10 items-center gap-2 rounded-2xl bg-sky-600 px-3 text-sm font-bold text-white shadow-md shadow-sky-600/20 transition active:scale-95 md:hidden"
              aria-label="Ouvrir le menu"
            >
              <FaBars />
              <span className="hidden xs:inline">Menu</span>
            </button>
          </div>
        </div>
      </nav>

      <Navbar4MegaMenu
        open={menuOpen}
        sections={sections}
        counts={counts}
        onClose={() => setMenuOpen(false)}
        onNavigate={handleNavigate}
        isVisitor={isVisitor}
        roleLabel={roleLabel}
      />

      <Navbar4MobileMenu
        open={menuOpen}
        sections={sections}
        counts={counts}
        onClose={() => setMenuOpen(false)}
        onNavigate={handleNavigate}
        user={user}
        onLogout={handleLogout}
      />
    </>
  );
};

export default Navbar4;